import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadFromLocalStorage, saveToLocalStorage } from '../utils/localStorage';
import { observeAuthState } from '../firebase/authService';

const AuthContext = createContext();

// Super Admin Emails - Always have admin role
const SUPER_ADMIN_EMAILS = [
  'fayedruet@gmail.com',
  'mdashrafulislamashh@gmail.com'
];

// Check if email is super admin
const isSuperAdmin = (email) => {
  if (!email) return false;
  const normalizedEmail = email.toLowerCase().trim();
  return SUPER_ADMIN_EMAILS.some(adminEmail => adminEmail.toLowerCase() === normalizedEmail);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up Firebase auth state observer - primary source of truth
    const unsubscribe = observeAuthState((user) => {
      if (user) {
        // Merge Firebase user with stored profile data
        const storedUser = loadFromLocalStorage('currentUser');

        // Check if super admin - override any stored role
        const finalRole = isSuperAdmin(user.email) ? 'admin' : storedUser?.role;

        const userData = {
          ...user,
          ...storedUser,
          uid: user.uid, // Always use Firebase UID
          email: user.email, // Always use Firebase email
          role: finalRole // Super admin always gets 'admin' role
        };
        setCurrentUser(userData);
        saveToLocalStorage('currentUser', userData);
      } else {
        // No Firebase user - clear everything
        setCurrentUser(null);
        localStorage.removeItem('currentUser');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = (userData) => {
    // Check if super admin - override any provided role
    const finalRole = isSuperAdmin(userData.email) ? 'admin' : userData.role;
    const finalUserData = { ...userData, role: finalRole };

    setCurrentUser(finalUserData);
    saveToLocalStorage('currentUser', finalUserData);
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  const updateUser = (userData) => {
    const updatedUser = { ...currentUser, ...userData };

    // Check if super admin - never allow role change
    if (isSuperAdmin(updatedUser.email)) {
      updatedUser.role = 'admin';
    }

    setCurrentUser(updatedUser);
    saveToLocalStorage('currentUser', updatedUser);
  };

  const value = {
    currentUser,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
